import { useId, useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, AlertCircle, Sparkles, ChevronDown, Check, X, Trash2 } from 'lucide-react';
import {
  addBugComment,
  deleteBug as deleteBugApi,
  fetchBug,
  fetchBugs,
  StaleAuthenticationError,
  setBugStatus,
} from '../../lib/api/bugs';
import { useT } from '../../i18n';
import { onReauthenticated } from '../../lib/api/reauthenticationSignal';
import { TICKET_STATUS, TICKET_STATUS_ORDER, migrateStatus, isActiveTicket } from './ticketStatus';
import { BugDetailModal } from './BugDetailModal';
import { useAsyncAction } from '../../lib/useAsyncAction';
import { ConfirmButton } from '../ui/ConfirmButton';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

// ─────────────────────────────────────────────────────────────────────
// BUG ARCHIVE VIEW — lists all reported bugs/feature-requests with status and
// kind filters, inline status dropdowns, comment previews, screenshot
// thumbnails, a full-screen image preview, and a detail modal. Extracted
// verbatim from the monolith (legacy/App.jsx). Reads/writes the 'bugs_v1'
// Storage key. No secrets here.
//
// Markup (classNames, Norwegian strings, structure) is preserved exactly,
// including the bare `confirm(...)` delete guard that came from the monolith.
// ─────────────────────────────────────────────────────────────────────

export interface BugArchiveViewProps {
  onBack?: any;
  embed?: any;
}

export function BugArchiveView({ onBack, embed = false }: BugArchiveViewProps) {
  const t = useT();
  const [bugs, setBugs] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all_active'); // ID from TICKET_STATUS, 'all_active', 'all_closed', or 'all'
  const [kindFilter, setKindFilter] = useState('all'); // 'all' | 'bug' | 'feature'
  const [selectedBug, setSelectedBug] = useState<any>(null); // open bug for detail view
  const previewTitleId = safeId('bug-image-title', useId());
  const [imagePreview, setImagePreview] = useState<any>(null); // full-screen image preview { src, title }
  const [copyStatus, setCopyStatus] = useState('');
  // ── THE HEAVY FIELDS, LOADED PER BUG (#878) ──────────────────────
  //
  // The list no longer carries `screenshot` / `consoleLog` — it is what the
  // badges poll every 2 seconds — so they are fetched one bug at a time: for a
  // row whose thumbnail is on screen, and for the bug opened in the detail
  // modal. Kept APART from `bugs` on purpose: `bugs` is what gets written
  // back, and the server keeps a field the write-back omits.
  const [attachments, setAttachments] = useState<
    Record<string, { screenshot: any; consoleLog: any }>
  >({});
  const requested = useRef(new Set<string>());
  const loadAttachments = (id: string) => {
    if (requested.current.has(id)) return;
    requested.current.add(id);
    void fetchBug(id)
      .then((full) => {
        // `fetchBug` answers a failed or missing read with null rather than
        // throwing, so null must free the id too — or the thumbnail stays
        // blank and reopening the bug never asks again (#893 review).
        if (!full) {
          requested.current.delete(id);
          return;
        }
        setAttachments((prev) => ({
          ...prev,
          [id]: { screenshot: full.screenshot ?? null, consoleLog: full.consoleLog ?? null },
        }));
      })
      .catch(() => {
        // Best-effort, like the thumbnail itself: let a later open try again.
        requested.current.delete(id);
      });
  };
  // A bug off the memory backend still carries its fields inline.
  const inline = (bug: any) => bug.screenshot !== undefined || bug.consoleLog !== undefined;
  const hasShot = (bug: any) => !!bug.screenshot || bug.hasScreenshot === true;
  const shotOf = (bug: any) => bug.screenshot || attachments[bug.id]?.screenshot || null;
  const withAttachments = (bug: any) =>
    inline(bug) || !attachments[bug.id] ? bug : { ...bug, ...attachments[bug.id] };

  useEffect(() => {
    let alive = true;
    let stopWaiting: (() => void) | null = null;
    const loadList = async () => {
      try {
        const list = await fetchBugs();
        if (alive) setBugs(list);
      } catch (e) {
        // TOO OLD A SESSION (#1141). The step-up prompt is already open — the
        // transport raised it — so wait for it and load again, rather than
        // leaving an empty list that reads as «no reports».
        if (e instanceof StaleAuthenticationError) {
          stopWaiting?.();
          stopWaiting = onReauthenticated(() => {
            stopWaiting?.();
            stopWaiting = null;
            void loadList();
          });
        } else {
          console.error('[Padel24] Kunne ikke laste bugs:', e);
        }
      }
      if (alive) setLoaded(true);
    };
    void loadList();
    return () => {
      alive = false;
      stopWaiting?.();
    };
  }, []);

  // Status helper must be declared before filteredBugs, which uses it.
  // Backward compat: old reports have only `fixed: bool` or the old 'fixed' status.
  const getStatus = (bug: any) => migrateStatus(bug);

  const filteredBugs = useMemo(() => {
    return bugs.filter((b) => {
      // Filter on status:
      //   'all'         → all tickets
      //   'all_active'  → everything except closed + rejected (default)
      //   'all_closed'  → only closed + rejected (both end statuses combined)
      //   <statusId>    → exact match on a single status
      const s = getStatus(b);
      if (statusFilter === 'all') {
        // no status filtering
      } else if (statusFilter === 'all_active') {
        // Active excludes solved/closed/rejected (all out-of-pipeline states)
        if (s === 'solved' || s === 'closed' || s === 'rejected') return false;
      } else if (statusFilter === 'all_closed') {
        // The closed bucket gathers solved + closed + rejected for an overview
        if (s !== 'solved' && s !== 'closed' && s !== 'rejected') return false;
      } else if (s !== statusFilter) {
        return false;
      }
      // Filter on type — old bugs without 'kind' count as 'bug'
      const itemKind = b.kind || 'bug';
      if (kindFilter !== 'all' && itemKind !== kindFilter) return false;
      return true;
    });
  }, [bugs, statusFilter, kindFilter]);

  // Thumbnails on screen, and the bug that is open, load their heavy fields.
  useEffect(() => {
    for (const b of filteredBugs) if (b.hasScreenshot && !inline(b)) loadAttachments(b.id);
    if (selectedBug && !inline(selectedBug)) loadAttachments(selectedBug.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBugs, selectedBug?.id]);

  // ============================================================
  // STATUS HANDLING — reports can have three states:
  //   'open' (default), 'fixed', 'rejected'
  //
  // Backward compat: old reports have only `fixed: bool`. We read both
  // formats in the UI, but now always write both status AND fixed to keep
  // the legacy fields consistent.
  // ============================================================
  //
  // ONE CHANGE, DERIVED ON THE SERVER (owner decision 2026-10-22). The legacy
  // mirror (`fixed`, `fixedAt`, `rejectedAt`) used to be computed here and the
  // whole list written back; the server owns that rule now, and answers with
  // what it wrote — which is what the row and the open ticket show.
  // ============================================================
  const setStatus = async (bugId: any, newStatus: any) => {
    if (!TICKET_STATUS[newStatus]) {
      console.warn('[Padel24] Ukjent status:', newStatus);
      return null;
    }
    // STORAGE FIRST, THEN STATE (CLAUDE.md) — and the failure is no longer
    // swallowed. A status that reverted in the database while the row still
    // showed the new one was indistinguishable from success (#369).
    const change = await setBugStatus(bugId, newStatus);
    setBugs((list) => list.map((b) => (b.id === bugId ? { ...b, ...change } : b)));
    return change;
  };

  // ── FEEDBACK ON THE ROW THAT IS CHANGING (#369) ──────────────────
  //
  // The controls live inside a `.map`, so one hook per row is not available —
  // the row being mutated is tracked instead, and the control it belongs to
  // reads its own state from that. Same three answers, per row: pending while
  // the write is in flight, gone when it lands, and the failure printed IN the
  // row rather than in a console the user never opens.
  const [busyBugId, setBusyBugId] = useState<string | null>(null);
  const [failedBugId, setFailedBugId] = useState<string | null>(null);
  const rowWrite = useAsyncAction(async (bugId: string, write: () => Promise<void>) => {
    setBusyBugId(bugId);
    setFailedBugId(null);
    try {
      await write();
    } catch (e) {
      setFailedBugId(bugId);
      throw e;
    } finally {
      setBusyBugId(null);
    }
  });

  // Add a comment to the thread. Author: 'user' (the reporter) or 'owner' (Andreas).
  // Used from both the list view (quick reply) and the detail modal (full thread).
  // The server mints the comment, migrates a legacy `ownerComment` into the
  // thread on the first reply, and answers with the whole thread.
  const addComment = async (bugId: any, text: any, author: 'owner' | 'user' = 'owner') => {
    if (!text || !text.trim()) return null;
    const comments = await addBugComment(bugId, text.trim(), author);
    setBugs((list) => list.map((b) => (b.id === bugId ? { ...b, comments } : b)));
    return comments;
  };

  // Kept for backward compat — toggles open ↔ closed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleFixed = async (bugId: any) => {
    const bug = bugs.find((b) => b.id === bugId);
    if (!bug) return;
    const current = getStatus(bug);
    await setStatus(bugId, current === 'closed' ? 'open' : 'closed');
  };

  const deleteBug = async (bugId: any) => {
    // The `confirm()` guard that used to be here did nothing in an iframe and
    // returned false, so the delete never ran (#589). The question is asked by
    // the button now — see the ConfirmButton below.
    await deleteBugApi(bugId);
    setBugs((list) => list.filter((b) => b.id !== bugId));
  };

  // Counters for the various categories — use the status concept
  const isBug = (b: any) => (b.kind || 'bug') === 'bug';
  const isFeature = (b: any) => (b.kind || 'bug') === 'feature';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isOpen = (b: any) => getStatus(b) === 'open';

  // Counts per status (all 7 individual ones + pseudo filters)
  const statusCounts: any = {};
  TICKET_STATUS_ORDER.forEach((s) => {
    statusCounts[s] = bugs.filter((b) => getStatus(b) === s).length;
  });
  statusCounts.all = bugs.length;
  statusCounts.all_active = bugs.filter((b) => {
    // Active = still needs attention. Includes open tickets
    // (open, discussion, in_progress, review, approved). Excludes
    // finished ones (solved, closed) and rejected ones (rejected).
    const s = getStatus(b);
    return s !== 'solved' && s !== 'closed' && s !== 'rejected';
  }).length;
  statusCounts.all_closed = bugs.filter((b) => {
    // Everything out of the active pipeline — solved, closed and rejected.
    const s = getStatus(b);
    return s === 'solved' || s === 'closed' || s === 'rejected';
  }).length;

  // "Active" = needs attention now (open + in_progress).
  // Excludes: paused (discussion, review) and finished (approved, closed, rejected).
  const activeCount = bugs.filter(isActiveTicket).length;

  // Bug/feature counts in the "all" type tab = active tickets of each type
  const bugCount = bugs.filter((b) => isActiveTicket(b) && isBug(b)).length;
  const featureCount = bugs.filter((b) => isActiveTicket(b) && isFeature(b)).length;

  return (
    <div className={embed ? '' : 'max-w-7xl mx-auto px-4 md:px-6 py-10 slide-up'}>
      {!embed && (
        <button
          onClick={onBack}
          className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        >
          <ArrowLeft size={12} /> Tilbake
        </button>
      )}

      {!embed && (
        <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <AlertCircle size={14} className="text-rose" strokeWidth={2.5} />
              <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
                Utviklingsmodus
              </span>
            </div>
            <h1 className="font-display text-3xl font-semibold mb-1">Bug &amp; forslag</h1>
            <p className="text-ink-2 text-sm">
              Rapporterte feil og funksjonsforslag. Huk av for å markere som fikset.
            </p>
          </div>

          <div className="flex items-center gap-1 border border-line rounded-full bg-paper p-0.5 text-xs overflow-x-auto scrollbar-thin">
            {[
              { id: 'all_active', label: 'Aktive', count: statusCounts.all_active },
              { id: 'open', label: TICKET_STATUS.open.short, count: statusCounts.open },
              {
                id: 'discussion',
                label: TICKET_STATUS.discussion.short,
                count: statusCounts.discussion,
              },
              {
                id: 'in_progress',
                label: TICKET_STATUS.in_progress.short,
                count: statusCounts.in_progress,
              },
              { id: 'review', label: TICKET_STATUS.review.short, count: statusCounts.review },
              { id: 'approved', label: TICKET_STATUS.approved.short, count: statusCounts.approved },
              { id: 'solved', label: TICKET_STATUS.solved.short, count: statusCounts.solved },
              { id: 'all_closed', label: 'Lukket', count: statusCounts.all_closed },
              { id: 'all', label: 'Alle', count: statusCounts.all },
            ].map((opt) => (
              <button
                key={opt.id}
                aria-pressed={statusFilter === opt.id}
                onClick={() => setStatusFilter(opt.id)}
                className={`px-2.5 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap ${
                  statusFilter === opt.id ? 'bg-ink text-white' : 'text-ink-2 hover:bg-bg-soft'
                }`}
              >
                {opt.label} ({opt.count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Status filter for embed mode */}
      {embed && (
        <div className="mb-3 flex items-center gap-1 border border-line rounded-full bg-paper p-0.5 text-xs overflow-x-auto scrollbar-thin">
          {[
            { id: 'all_active', label: 'Aktive', count: statusCounts.all_active },
            { id: 'open', label: TICKET_STATUS.open.short, count: statusCounts.open },
            {
              id: 'discussion',
              label: TICKET_STATUS.discussion.short,
              count: statusCounts.discussion,
            },
            {
              id: 'in_progress',
              label: TICKET_STATUS.in_progress.short,
              count: statusCounts.in_progress,
            },
            { id: 'review', label: TICKET_STATUS.review.short, count: statusCounts.review },
            { id: 'approved', label: TICKET_STATUS.approved.short, count: statusCounts.approved },
            { id: 'all_closed', label: 'Lukket', count: statusCounts.all_closed },
            { id: 'all', label: 'Alle', count: statusCounts.all },
          ].map((opt) => (
            <button
              key={opt.id}
              aria-pressed={statusFilter === opt.id}
              onClick={() => setStatusFilter(opt.id)}
              className={`px-2.5 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap ${
                statusFilter === opt.id ? 'bg-ink text-white' : 'text-ink-2 hover:bg-bg-soft'
              }`}
            >
              {opt.label} ({opt.count})
            </button>
          ))}
        </div>
      )}

      {/* Type filter: all / bugs / features */}
      {statusFilter === 'open' && (
        <div className="mb-4 flex items-center gap-1 border-b border-line pb-0">
          <button
            aria-pressed={kindFilter === 'all'}
            onClick={() => setKindFilter('all')}
            className={`px-3 py-2 text-xs font-semibold transition-colors border-b-2 -mb-px ${
              kindFilter === 'all'
                ? 'border-ink text-ink'
                : 'border-transparent text-ink-3 hover:text-ink-2'
            }`}
          >
            Alle ({activeCount})
          </button>
          <button
            aria-pressed={kindFilter === 'bug'}
            onClick={() => setKindFilter('bug')}
            className={`px-3 py-2 text-xs font-semibold transition-colors border-b-2 -mb-px flex items-center gap-1 ${
              kindFilter === 'bug'
                ? 'border-rose text-rose'
                : 'border-transparent text-ink-3 hover:text-ink-2'
            }`}
          >
            <AlertCircle size={11} /> Bugs ({bugCount})
          </button>
          <button
            aria-pressed={kindFilter === 'feature'}
            onClick={() => setKindFilter('feature')}
            className={`px-3 py-2 text-xs font-semibold transition-colors border-b-2 -mb-px flex items-center gap-1 ${
              kindFilter === 'feature'
                ? 'border-lime-deep text-lime-deep'
                : 'border-transparent text-ink-3 hover:text-ink-2'
            }`}
          >
            <Sparkles size={11} /> Forslag ({featureCount})
          </button>
        </div>
      )}

      {!loaded && <div className="text-center text-sm text-ink-3 py-8">Laster bugs...</div>}

      {loaded && filteredBugs.length === 0 && (
        <div className="card p-8 text-center text-sm text-ink-3 italic">
          {statusFilter === 'fixed'
            ? 'Ingen er markert som fikset ennå.'
            : statusFilter === 'rejected'
              ? 'Ingen er avvist.'
              : 'Ingen åpne meldinger! 🎉'}
        </div>
      )}

      {loaded && filteredBugs.length > 0 && (
        <div className="space-y-2">
          {filteredBugs.map((bug) => {
            const status = getStatus(bug);
            const statusInfo = TICKET_STATUS[status];
            const isDimmed = status === 'closed' || status === 'rejected';
            // Build the combined comment list — include the legacy ownerComment if present
            const commentList = (() => {
              const list = Array.isArray(bug.comments) ? [...bug.comments] : [];
              if (bug.ownerComment && list.length === 0) {
                list.push({
                  id: `legacy_${bug.id}`,
                  text: bug.ownerComment,
                  author: 'owner',
                  createdAt: bug.ownerCommentAt || bug.createdAt,
                });
              }
              return list;
            })();
            const lastComment = commentList[commentList.length - 1];

            return (
              <div
                key={bug.id}
                className={`card-elev p-4 transition-all ${isDimmed ? 'opacity-60' : ''} hover:shadow-md`}
              >
                <div className="flex items-start gap-3">
                  {/* Status pill with an inline dropdown for changing status from the list.
                      When the user clicks the pill, a small menu opens with all
                      statuses so they can be changed directly without opening the detail modal.

                      The pill, its menu and the delete button below used to
                      stop every click from bubbling. Nothing above them
                      listens for one — the row itself is not clickable, only
                      its text is — so those handlers were dead, and they made
                      a `<details>`, a `<div>` and a `<span>` look interactive
                      to assistive tech. Removed (#900); nothing changes. */}
                  <div className="flex-shrink-0 relative">
                    <details className="group">
                      <summary
                        className="cursor-pointer px-2 py-1 rounded-md flex items-center gap-1 text-[11px] font-bold list-none transition-opacity hover:opacity-80"
                        style={{
                          background: statusInfo.bg,
                          color: statusInfo.fg,
                          border: `1.5px solid ${statusInfo.border}`,
                        }}
                        title="Klikk for å endre status"
                      >
                        <span className="whitespace-nowrap">{statusInfo.short}</span>
                        <ChevronDown size={9} strokeWidth={3} />
                      </summary>
                      <div className="absolute z-20 mt-1 bg-paper border border-line rounded-lg shadow-lg p-1 min-w-[140px]">
                        {TICKET_STATUS_ORDER.map((sId) => {
                          const s = TICKET_STATUS[sId];
                          const isCurrent = sId === status;
                          return (
                            <button
                              key={sId}
                              disabled={busyBugId === bug.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                void rowWrite.run(bug.id, async () => {
                                  await setStatus(bug.id, sId);
                                });
                                // Close the dropdown
                                (e.currentTarget.closest('details') as any).open = false;
                              }}
                              className={`w-full text-left px-2 py-1.5 rounded text-[11px] font-medium flex items-center gap-2 transition-colors ${
                                isCurrent ? 'bg-bg-soft' : 'hover:bg-bg-soft'
                              }`}
                            >
                              <span
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ background: s.border }}
                              />
                              <span style={{ color: s.fg }}>{s.label}</span>
                              {isCurrent && <Check size={10} className="ml-auto text-ink-3" />}
                            </button>
                          );
                        })}
                      </div>
                    </details>
                  </div>

                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => setSelectedBug(bug)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      // Space as well as Enter: it says it is a button (#900).
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedBug(bug);
                      }
                    }}
                    title="Klikk for å se detaljer"
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {bug.kind === 'feature' ? (
                        <span
                          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1"
                          style={{ background: '#dcfce7', color: '#15803d' }}
                        >
                          <Sparkles size={9} strokeWidth={3} /> FORSLAG
                        </span>
                      ) : (
                        <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-soft text-rose">
                          BUG
                        </span>
                      )}
                      <span className="font-mono text-[10px] text-ink-3">{bug.page}</span>
                      <span className="font-mono text-[10px] text-ink-4">·</span>
                      <span className="font-mono text-[10px] text-ink-3">
                        {new Date(bug.createdAt).toLocaleDateString('no-NO', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </span>
                      {/* WHICH CLUB (owner decision 2026-10-22): the administrators'
                          list spans every club, so each row says whose it is. */}
                      {bug.tenantId && (
                        <span className="font-mono text-[10px] text-ink-2" data-testid="bug-club">
                          {t('bugs.archive.club', {
                            name: bug.organizationName || t('bugs.archive.unknownClub'),
                          })}
                        </span>
                      )}
                      {bug.reporter?.name && (
                        <span className="font-mono text-[10px] text-ink-3">
                          {t('bugs.archive.reporter', { name: String(bug.reporter.name) })}
                        </span>
                      )}
                      {hasShot(bug) && (
                        <span
                          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-bg-soft text-ink-2 flex items-center gap-1"
                          title="Skjermbilde vedlagt"
                        >
                          📎 Bilde
                        </span>
                      )}
                      {commentList.length > 0 && (
                        <span
                          className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-bg-soft text-ink-2 flex items-center gap-1"
                          title={`${commentList.length} kommentar${commentList.length === 1 ? '' : 'er'}`}
                        >
                          💬 {commentList.length}
                        </span>
                      )}
                    </div>
                    <div
                      className={`font-display text-base font-semibold leading-tight mb-1 ${isDimmed ? 'line-through' : ''}`}
                    >
                      {bug.title}
                    </div>
                    <div className="text-xs text-ink-2 whitespace-pre-wrap line-clamp-2">
                      {bug.description}
                    </div>

                    {/* Last comment preview — shown as an inset block */}
                    {lastComment && (
                      <div
                        className="mt-2 px-3 py-2 rounded text-[11px] leading-relaxed"
                        style={{
                          background:
                            lastComment.author === 'owner'
                              ? 'rgba(220, 252, 231, 0.5)'
                              : 'rgba(219, 234, 254, 0.5)',
                          borderLeft: `3px solid ${lastComment.author === 'owner' ? '#86efac' : '#93c5fd'}`,
                        }}
                      >
                        <div className="font-mono text-[9px] uppercase tracking-wider text-ink-3 mb-0.5 flex items-center justify-between">
                          <span>
                            {lastComment.author === 'owner' ? 'Svar fra Andreas' : 'Fra rapportør'}
                          </span>
                          {commentList.length > 1 && (
                            <span className="text-ink-4">+{commentList.length - 1} eldre</span>
                          )}
                        </div>
                        <div className="text-ink-2 whitespace-pre-wrap line-clamp-2">
                          {lastComment.text}
                        </div>
                      </div>
                    )}

                    <div className="text-[10px] text-ink-3 mt-1.5 font-mono">
                      Klikk for detaljer →
                    </div>
                  </div>

                  {/* Screenshot thumbnail — clicking opens the full-screen preview.
                      A real button (#900), so a keyboard can open it too. */}
                  {hasShot(bug) && (
                    <button
                      type="button"
                      className="flex-shrink-0 w-16 h-16 rounded border border-line overflow-hidden bg-bg-soft cursor-zoom-in hover:border-ink-4 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        const src = shotOf(bug);
                        if (src) setImagePreview({ src, title: bug.title });
                      }}
                      title="Klikk for å se full størrelse"
                    >
                      {/* Empty box until the per-bug read lands (#878). */}
                      {shotOf(bug) && (
                        <img
                          src={shotOf(bug)}
                          alt="Skjermbilde-thumbnail"
                          className="w-full h-full object-cover"
                        />
                      )}
                    </button>
                  )}

                  <span>
                    <ConfirmButton
                      disabled={busyBugId === bug.id}
                      onConfirm={() => void rowWrite.run(bug.id, () => deleteBug(bug.id))}
                      question="Slett denne permanent?"
                      confirmLabel="Slett"
                      className="flex-shrink-0 p-2 rounded-full text-ink-3 hover:bg-rose-soft hover:text-rose transition-colors disabled:opacity-40"
                      title={busyBugId === bug.id ? 'Lagrer…' : 'Slett rapport'}
                    >
                      <Trash2 size={14} />
                    </ConfirmButton>
                  </span>
                </div>
                {/* IN THE ROW, not in a banner (#369). The write failed and this
                    row still shows the old state — which is the truth, and the
                    user has to be told rather than left to infer it from a row
                    that did not change. */}
                {failedBugId === bug.id && rowWrite.error && (
                  <div role="alert" className="mt-2 text-[11px] font-medium text-rose">
                    Endringen ble ikke lagret. {rowWrite.error}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Full-screen image preview modal — a `Dialog` (#900), named by the
          ticket's title. It said «trykk Esc for å lukke» and nothing listened
          for Escape; now it does. It can open from the detail modal, on top of
          it, and is then the innermost dialog: Escape closes only the picture,
          and focus goes back to the screenshot that opened it. */}
      {imagePreview && (
        <Dialog
          labelledBy={previewTitleId}
          onClose={() => setImagePreview(null)}
          showClose={false}
          backdropClassName="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85"
          className="relative max-w-6xl max-h-full flex flex-col"
        >
          <div className="flex items-center justify-between mb-2 text-white">
            <span id={previewTitleId} className="text-sm font-mono">
              {imagePreview.title}
            </span>
            <button
              type="button"
              onClick={() => setImagePreview(null)}
              className="text-white hover:bg-white/20 rounded-full p-1.5 transition-colors"
              title="Lukk (Esc)"
            >
              <X size={20} />
            </button>
          </div>
          <img
            src={imagePreview.src}
            alt={imagePreview.title}
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
          />
          <div className="text-center mt-2 text-white/70 text-xs">
            Klikk utenfor bildet eller trykk Esc for å lukke
          </div>
        </Dialog>
      )}

      {/* Bug detail modal */}
      {selectedBug && (
        <BugDetailModal
          bug={withAttachments(selectedBug)}
          onClose={() => setSelectedBug(null)}
          onSetStatus={async (newStatus: any) => {
            const change = await setStatus(selectedBug.id, newStatus);
            // The open ticket shows what the server wrote.
            if (change) setSelectedBug((prev: any) => ({ ...prev, ...change }));
          }}
          onAddComment={async (text: any, author: 'owner' | 'user' = 'owner') => {
            const comments = await addComment(selectedBug.id, text, author);
            if (comments) setSelectedBug((prev: any) => ({ ...prev, comments }));
          }}
          onOpenImage={(src: any, title: any) => setImagePreview({ src, title })}
          currentStatus={getStatus(selectedBug)}
          onDelete={() => {
            deleteBug(selectedBug.id);
            setSelectedBug(null);
          }}
          copyStatus={copyStatus}
          setCopyStatus={setCopyStatus}
        />
      )}
    </div>
  );
}
